export const MONTH_MAP: { [key: number]: string } = {
    1: "Jan",
    2: "Feb",
    3: "Mar",
    4: "Apr",
    5: "May",
    6: "Jun",
    7: "Jul",
    8: "Aug",
    9: "Sep",
    10: "Oct",
    11: "Nov",
    12: "Dec"
};

const isValidDate = (date?: Date): date is Date => date instanceof Date && !Number.isNaN(date.getTime());

/**
 * Formats the elapsed time between two timestamps as a compact "1h 4m" / "3m 24s" / "45s" string.
 *
 * Returns `null` when the span cannot be computed — either endpoint missing or unparseable, or an
 * end that precedes its start — so callers render their own placeholder rather than something like
 * "NaNs" or a negative duration.
 */
export const getFormattedDuration = ({ start, end }: { start?: Date; end?: Date }): string | null => {
    if (!isValidDate(start) || !isValidDate(end)) {
        return null;
    }

    const elapsedMs = end.getTime() - start.getTime();

    if (elapsedMs < 0) {
        return null;
    }

    const totalSeconds = Math.floor(elapsedMs / 1000);

    if (totalSeconds < 1) {
        return "<1s";
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }

    if (minutes > 0) {
        return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }

    return `${seconds}s`;
};

export const getFormattedDate = ({ date, hideTimestamp }: { date: Date; hideTimestamp?: boolean }) => {
    if (!date) {
        return null;
    }

    const d = new Date(date.toString());
    const local = d.toLocaleString();
    const timestamp = local.split(", ")[1] || "";

    const monthNumber = d.getUTCMonth() + 1;
    const month: string = MONTH_MAP[monthNumber];
    const day = d.getUTCDate();
    const year = d.getUTCFullYear();
    const mmddyyyy = `${month} ${day}, ${year}`;
    return hideTimestamp ? mmddyyyy : `${mmddyyyy} at ${timestamp}`;
};
